import { z } from "zod";

/**
 * The deterministic management policy that runs after a signal is live. These fire from
 * real MetaApi events, not from the LLM, so behaviour is predictable and auditable —
 * the LLM only gets involved when the channel posts an ambiguous follow-up message.
 */
export const ruleTriggerSchema = z.enum([
  "ENTRY_FILLED",
  "TP1_HIT",
  "TP2_HIT",
  "TP3_HIT",
  "SL_HIT",
  "SIGNAL_CLOSED",
]);
export type RuleTrigger = z.infer<typeof ruleTriggerSchema>;

const slots = z.array(z.number().int().min(1).max(3)).min(1).optional();

/**
 * Rule actions are all relative (pips, slots) rather than absolute prices, so one rule
 * set applies to every signal a channel posts. Absolute-price changes come from the
 * decider handling a specific follow-up message.
 */
export const ruleActionSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("MOVE_SL_TO_BE"),
    /**
     * Positive moves the stop into profit. A stop exactly at the entry is slightly negative
     * once commission and swap are counted, so a small positive value is what actually
     * produces a floor at zero.
     */
    offsetPips: z.number().min(-1000).max(1000).default(0),
    /**
     * A buffer in multiples of the CURRENT spread, applied in the ADVERSE direction: the
     * stop sits below entry for a buy, so a spread spike or a shallow retest does not tag
     * it. This is the whole point — a stop parked exactly at entry gets hit by the
     * retest-then-recover move that would have gone on to pay, and that "fake reverse" is
     * the dominant cost of naive breakeven.
     *
     * Spread-relative rather than a fixed pip count because one number cannot serve both
     * instruments: this broker reports gold with pipSize 0.01, a tenth of what a signal
     * channel means by a gold pip. Spread is the one volatility proxy already available
     * live, with no new data pipeline.
     *
     * ATR-based sizing (0.25 x ATR14 on M15) is the better version and is the documented
     * upgrade path — it needs a candle-fetching pipeline that does not exist yet
     * (`getHistoricalCandles` is called nowhere), so it waits for backlog B1.
     */
    bufferSpreads: z.number().min(0).max(20).default(2),
    slots,
  }),
  z.object({
    type: z.literal("MOVE_SL_TO_TP"),
    /** Trails the stop up to an already-hit take-profit level. */
    tpSlot: z.number().int().min(1).max(3),
    slots,
  }),
  z.object({
    type: z.literal("MOVE_SL_TO_R"),
    /**
     * The stop as a multiple of the trade's ORIGINAL risk, measured from the least
     * favourable fill: `-1` leaves it where the signal put it, `0` is breakeven, `+0.5`
     * banks half the risk. Fable's P2/P3 — moving all the way to breakeven at TP1 is only
     * one point on this line, and not obviously the best one, because the retest that
     * precedes most continuations lives in exactly that zone.
     *
     * R is the distance from entry to the signal's OWN stated stop, not the padded stop
     * resting at the broker. Same reference the breakeven buffer's risk clamp uses, and it
     * has to be said out loud because the two differ by the per-channel padding.
     */
    rMultiple: z.number().min(-1).max(5),
    slots,
  }),
  z.object({ type: z.literal("CLOSE_REMAINING"), slots }),
  z.object({
    type: z.literal("PARTIAL_CLOSE"),
    pct: z.number().positive().max(100),
    slots,
  }),
  z.object({
    type: z.literal("SET_TRAILING"),
    /** Runs server-side at MetaApi, so it keeps working if this app is down. */
    distancePips: z.number().positive().max(100000),
    slots,
  }),
  z.object({ type: z.literal("CANCEL_PENDING"), slots }),
]);
export type RuleAction = z.infer<typeof ruleActionSchema>;

export const ruleSchema = z.object({
  trigger: ruleTriggerSchema,
  actions: z.array(ruleActionSchema).min(1),
});
export type Rule = z.infer<typeof ruleSchema>;

export const ruleSetSchema = z.array(ruleSchema);
export type RuleSet = z.infer<typeof ruleSetSchema>;

/**
 * However wide the spread goes during a news spike or the rollover window, the adverse
 * buffer never gives back more than this fraction of the trade's original risk. Without
 * the clamp, a 40-pip rollover spread on a 30-pip-risk trade would push the "breakeven"
 * stop further away than the stop it replaced.
 */
export const BE_BUFFER_MAX_RISK_FRACTION = 0.25;

/**
 * Seeded global policy: lock in breakeven once the first target pays out, then trail the
 * stop to TP1 when the second one does. TP3 is rarely reached, so the remaining runner is
 * left to either hit it or stop out at a guaranteed profit.
 *
 * The `bufferSpreads: 2` on the breakeven step is deliberate and is the one part of this
 * that is not obvious. Breakeven-at-exactly-entry is a measurably poor default: after TP1
 * fills, the remaining position is in a race between TP2 and the stop, and a stop sitting
 * on the entry price is tagged by the ordinary retest that precedes most continuations.
 * The buffer trades a small, bounded amount of protection for a much lower chance of being
 * stopped out of a trade that was going to pay. Whether it is the RIGHT buffer for a given
 * channel is an empirical question (backlog B1) — this is the cold-start default, correct
 * until there is enough per-channel history to fit something better, which is a long time.
 */
export const DEFAULT_RULE_SET: RuleSet = [
  { trigger: "TP1_HIT", actions: [{ type: "MOVE_SL_TO_BE", offsetPips: 0, bufferSpreads: 2 }] },
  { trigger: "TP2_HIT", actions: [{ type: "MOVE_SL_TO_TP", tpSlot: 1 }] },
  { trigger: "SIGNAL_CLOSED", actions: [{ type: "CANCEL_PENDING" }] },
];
